import { useMemo, useRef, useState } from 'react';
import { QUOTE_MAX_LENGTH, type Task, type TaskKind } from '@shared/domain.ts';
import { useRigg } from '@/data/RiggProvider.tsx';
import { api } from '@/lib/api.ts';
import { useAttachments } from '@/lib/uploads.ts';
import { useMediaQuery } from '@/lib/useMediaQuery.ts';
import { AttachButton, AttachmentPills } from './Attachments.tsx';
import { kindOptions } from './TaskFacts.tsx';
import { Button, Checkbox, Dialog, Select, Textarea, Tip, useToast, type Option } from './ui.tsx';

/** A placeholder title, the same way the server falls back when nothing else derives one. */
function guessTitle(prompt: string): string {
  const firstLine = prompt.split('\n')[0]!.trim();
  return firstLine.length > 72 ? `${firstLine.slice(0, 69)}…` : firstLine;
}

/**
 * The new-task box.
 *
 * One field. The first line becomes the title, the whole thing is the brief —
 * because being made to fill in a title *and* a description to say one thing is
 * the friction that stops people raising small tasks, and small tasks are what
 * this whole loop is for.
 *
 * It sits below the list rather than above it: reading what is happening comes
 * first.
 */
export function Composer({ onCreated }: { onCreated: (taskId: string) => void }) {
  const { data, me, project, projects, addOptimisticTask, removeOptimisticTask } = useRigg();
  const { notify } = useToast();
  const box = useRef<HTMLTextAreaElement>(null);
  // Below `sm` the full form does not fit without pushing the list off
  // screen, so it lives behind a "+" instead of sitting open under the list.
  const isMobile = useMediaQuery('(max-width: 639px)');
  const [open, setOpen] = useState(false);

  const [prompt, setPrompt] = useState('');
  const [agentId, setAgentId] = useState('auto');
  const [repoId, setRepoId] = useState('auto');
  const [category, setCategory] = useState('none');
  const [kind, setKind] = useState('none');
  // The requester's exact words, when relaying someone else's ask. Shown on
  // the board only — never part of the brief. Tucked away because most tasks
  // are the typist's own words and need nothing here.
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quote, setQuote] = useState('');
  const [autoMerge, setAutoMerge] = useState(false);
  const [smart, setSmart] = useState(false);
  const [sending, setSending] = useState(false);
  const [dragging, setDragging] = useState(false);
  // Only asked for on the "All projects" board — a single project already
  // says which project a new task belongs to, so this stays unused there.
  const [targetProjectId, setTargetProjectId] = useState('');

  // The project this task will actually be created in. On a single project
  // it is simply that project; on "All projects" there is no ambient one, so
  // creating a task means picking it first — everything below (which
  // routines and repositories are even offered, whether Send is enabled)
  // waits on this.
  const activeProject = project ?? projects.find((p) => p.id === targetProjectId) ?? null;
  const noProject = !activeProject;

  const {
    items: attachments,
    onDrop,
    onDragOver,
    onPaste,
    attach,
    remove: removeAttachment,
    clear: clearAttachments,
    restore: restoreAttachments,
    compose,
    uploading,
    hasAttachment,
  } = useAttachments({ projectId: activeProject?.id, notify });

  // Scoped to the chosen project rather than shown as-is: `data.agents` and
  // friends hold every project's rows at once on the "All projects" board, and
  // an agent registered on a different project cannot run this one's work.
  // No project chosen yet means no rows, not every project's rows — the
  // filter answers "which agent runs *this* task", and there is no task's
  // project to answer that for until one is picked.
  const agents = useMemo(
    () =>
      Object.values(data.agents)
        .filter((a) => activeProject && a.project_id === activeProject.id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data.agents, activeProject],
  );
  const repos = useMemo(
    () =>
      Object.values(data.repos)
        .filter((r) => activeProject && r.project_id === activeProject.id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data.repos, activeProject],
  );
  const categories = useMemo(
    () =>
      Object.values(data.categories)
        .filter((c) => activeProject && c.project_id === activeProject.id)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [data.categories, activeProject],
  );

  // A design worker is never dispatched to, so it is not one of the answers to
  // "which routine runs this" — offering it would be offering a send that the
  // server refuses.
  const routines = useMemo(() => agents.filter((a) => a.kind !== 'design'), [agents]);

  // Offered only where it can happen. A checkbox that silently does nothing is
  // worse than an absent one: the promise it makes is the whole feature.
  // Checking the box for a project with no smart routine registered would be
  // a promise "Auto" cannot keep.
  const hasSmartAgent = routines.some((a) => a.intelligence === 'smart');

  const agentOptions: Option[] = [
    // No name next to it. Who has room is decided on the server, right before
    // sending — after checking the subscription live — and a name guessed here
    // from a stale reading is exactly how "Auto · Safari" came to be shown
    // while the server sent the task somewhere else.
    { value: 'auto', label: 'Auto — most room' },
    ...routines.map((agent) => ({
      value: agent.id,
      label: agent.name,
      hint: agent.paused
        ? 'paused'
        : agent.last_result === 'error'
          ? 'last send failed'
          : agent.intelligence === 'smart'
            ? 'smart'
            : undefined,
    })),
  ];

  const repoOptions: Option[] = [
    { value: 'auto', label: repos.length ? "Routine's repo" : 'No repositories' },
    ...repos.map((repo) => ({ value: repo.id, label: `${repo.owner}/${repo.name}` })),
  ];

  const categoryOptions: Option[] = [
    { value: 'none', label: 'No category' },
    ...categories.map((c) => ({ value: c.label, label: c.label })),
  ];

  const send = async () => {
    const text = compose(prompt.trim());
    if (!text || !activeProject) return;

    // What it takes to put the row on the board before the server has seen
    // it. A routine picked by name is shown as picked; "auto" shows nobody
    // until the server answers with the one it chose.
    const resolvedAgentId = agentId === 'auto' ? null : agentId;
    const resolvedRepoId =
      repoId === 'auto' ? (agentId === 'auto' ? null : data.agents[agentId]?.repo_id) ?? null : repoId;
    const resolvedCategoryId =
      category === 'none' ? null : (categories.find((c) => c.label === category)?.id ?? null);
    const resolvedKind = kind === 'none' ? null : (kind as TaskKind);
    // Only while the field is open: a collapsed box sends nothing it hides.
    const resolvedQuote = (quoteOpen && quote.trim()) || null;
    const now = new Date().toISOString();
    // Generated up front and sent to the server as the row's real id, so the
    // confirmed task lands under the same identity the optimistic one is
    // already showing under. Without that, Realtime's insert of the real row
    // (which can beat the round trip back) and this placeholder are two
    // different ids — both on screen until the round trip finally resolves
    // and cleans the placeholder up. Same id means the upsert just replaces
    // it in place, so there is never a moment with both.
    const id = crypto.randomUUID();
    const optimistic: Task = {
      id,
      ref: 0,
      project_id: activeProject.id,
      repo_id: resolvedRepoId,
      category_id: resolvedCategoryId,
      title: guessTitle(text),
      prompt: text,
      status: 'fresh',
      source: 'human',
      kind: resolvedKind,
      origin: 'person',
      origin_ref: null,
      quote: resolvedQuote,
      created_by: me?.id ?? null,
      agent_id: resolvedAgentId,
      auto_merge: autoMerge,
      smart,
      branch: null,
      pr_url: null,
      pr_number: null,
      pr_state: null,
      pr_draft: null,
      reviewed_sha: null,
      merge_nudged_sha: null,
      merge_nudged_at: null,
      merge_nudge_count: 0,
      status_note: null,
      status_note_at: null,
      last_message: null,
      agent_state: 'idle',
      answered_at: null,
      agent_activity: null,
      agent_activity_at: null,
      agent_heartbeat_at: null,
      agent_quiet_until: null,
      claim_code_hash: null,
      claim_expires_at: null,
      claimed_at: null,
      ci_state: 'none',
      ci_summary: null,
      ci_url: null,
      ci_updated_at: null,
      links: [],
      images: [],
      signed_off_by: null,
      signed_off_at: null,
      approved_by: null,
      approved_at: null,
      dispatch_attempts: 0,
      last_error: null,
      queued_at: null,
      queue_reason: null,
      queue_until: null,
      queue_retry_at: null,
      queue_failures: 0,
      queue_agent_id: null,
      created_at: now,
      updated_at: now,
      dispatched_at: null,
      first_preview_at: null,
      first_reviewable_at: null,
      reopened_at: null,
      merged_at: null,
      deployed_at: null,
      archived_at: null,
      deleted_at: null,
      snoozed_at: null,
      design_pass: false,
      design_state: null,
      design_message: null,
      design_then: null,
      design_requested_at: null,
      design_agent_id: null,
      design_claimed_at: null,
      design_finished_at: null,
      design_error: null,
      design_pass_count: 0,
      design_activity: null,
      design_activity_at: null,
      design_note: null,
      design_note_at: null,
      design_quiet_until: null,
      risk: null,
      merge_waived_by: null,
      merge_held_by: null,
    };

    const savedPrompt = prompt;
    const savedAutoMerge = autoMerge;
    const savedSmart = smart;
    const savedKind = kind;
    const savedQuote = quote;
    const savedAttachments = attachments;

    // Clear and show the new row now — waiting for the round trip is the
    // sluggishness this exists to remove. A failure below puts all three back.
    setPrompt('');
    setAutoMerge(false);
    setSmart(false);
    setKind('none');
    setQuote('');
    setQuoteOpen(false);
    clearAttachments();
    addOptimisticTask(optimistic);

    setSending(true);
    try {
      const result = await api.submit({
        id,
        project_id: activeProject.id,
        prompt: text,
        // "auto" stays null on the wire — the server picks, by subscription
        // room, after a live check.
        repo_id: repoId === 'auto' ? null : repoId,
        category: category === 'none' ? null : category,
        agent_id: agentId === 'auto' ? null : agentId,
        auto_merge: autoMerge,
        smart,
        kind: resolvedKind,
        quote: resolvedQuote,
      });
      // Same id as the placeholder — this just refreshes it in place with the
      // confirmed fields (real ref, resolved agent/repo, ...), whether or not
      // Realtime's own insert for this row has already landed.
      addOptimisticTask(result.task);
      onCreated(result.task.id);
      // On the phone the form was a sheet over the list, not part of it —
      // once the task is on the board there is nothing left to look at here.
      if (isMobile) setOpen(false);
      if (result.queued) {
        notify(result.error ? 'The routine did not accept it — queued' : 'Queued', {
          body: result.error
            ? `${result.error} — Rigg will keep trying, and send it elsewhere if another routine is free.`
            : `${result.task.queue_reason ?? 'Nobody can take it yet'}. It goes out as soon as a routine can.`,
          ...(result.error ? { tone: 'bad' as const } : {}),
        });
      } else if (!result.dispatched) {
        notify('The routine did not accept it', {
          body: result.error ?? 'It is saved — use Send it again once the endpoint is back.',
          tone: 'bad',
        });
      }
    } catch (error) {
      removeOptimisticTask(optimistic);
      setPrompt(savedPrompt);
      setAutoMerge(savedAutoMerge);
      setSmart(savedSmart);
      setKind(savedKind);
      setQuote(savedQuote);
      setQuoteOpen(Boolean(savedQuote));
      restoreAttachments(savedAttachments);
      notify('Could not create the task', { body: (error as Error).message, tone: 'bad' });
    } finally {
      setSending(false);
      // Nothing to return focus to once the sheet that held it is closing.
      if (!isMobile) box.current?.focus();
    }
  };

  const noAgents = routines.length === 0;
  const blocked = noProject || noAgents;

  const form = (
    <>
      {/*
        Only asked on the "All projects" board — a single project already
        answers this, and everything below (which routines, which
        repositories) waits on the answer, so it leads the form rather than
        sitting among the other options.
      */}
      {!project ? (
        <div className="mb-2.5">
          <Select
            size="sm"
            value={targetProjectId}
            onChange={(id) => {
              setTargetProjectId(id);
              // Whatever was picked — the agent/repo/category, and anything
              // already uploaded — belonged to the previous project (or to no
              // project at all). An attachment in particular was signed and
              // stored against that project's own access check; carrying it
              // over would submit it as part of a task in a project it was
              // never authorized against.
              setAgentId('auto');
              setRepoId('auto');
              setCategory('none');
              clearAttachments();
            }}
            options={[
              { value: '', label: 'Choose a project' },
              ...projects.map((p) => ({ value: p.id, label: p.name })),
            ]}
            aria-label="Project"
          />
        </div>
      ) : null}
      <div
        className="relative"
        onDragEnter={(event) => {
          if (event.dataTransfer.types.includes('Files')) setDragging(true);
        }}
        onDragOver={onDragOver}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false);
        }}
        onDrop={(event) => {
          setDragging(false);
          onDrop(event);
        }}
      >
        <Textarea
          ref={box}
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
              event.preventDefault();
              void send();
            }
          }}
          onPaste={onPaste}
          placeholder={
            noProject
              ? 'Choose a project above before sending work'
              : noAgents
                ? 'Register a routine under Agents before sending work'
                : 'What should the agent do? Drag in files, or paste a screenshot.'
          }
          disabled={blocked}
          aria-label="What should the agent do?"
          className="min-h-14 rounded-xl sm:text-[13.5px]"
        />
        {dragging ? (
          <div className="rg-anim pointer-events-none absolute inset-0 grid place-items-center rounded-xl border-2 border-dashed border-accent bg-app/90 text-[13px] font-semibold text-accent">
            Drop to attach
          </div>
        ) : null}
      </div>
      <AttachmentPills items={attachments} onRemove={removeAttachment} />
      {quoteOpen ? (
        <Textarea
          value={quote}
          onChange={(event) => setQuote(event.target.value)}
          maxLength={QUOTE_MAX_LENGTH}
          placeholder="Requester's exact words, verbatim — shown on the board, never sent to the routine"
          aria-label="Requester's exact words"
          className="mt-2 min-h-12 rounded-xl sm:text-[13px]"
        />
      ) : null}

      <div className="mt-2.5 flex flex-wrap items-center gap-3">
        <AttachButton onFiles={attach} disabled={blocked} />
        <Select
          size="sm"
          value={agentId}
          onChange={setAgentId}
          options={agentOptions}
          aria-label="Which routine"
          className="py-1.5"
        />
        {repos.length > 0 ? (
          <Select
            size="sm"
            value={repoId}
            onChange={setRepoId}
            options={repoOptions}
            aria-label="Which repository"
            className="py-1.5"
          />
        ) : null}
        <Select
          size="sm"
          value={category}
          onChange={setCategory}
          options={categoryOptions}
          aria-label="Category"
          className="py-1.5"
        />
        <Select
          size="sm"
          value={kind}
          onChange={setKind}
          options={kindOptions('No kind')}
          aria-label="Kind"
          className="py-1.5"
        />
        <button
          type="button"
          onClick={() => setQuoteOpen((open) => !open)}
          disabled={blocked}
          aria-expanded={quoteOpen}
          className="rg-focus cursor-pointer text-[11.5px] font-semibold text-mute hover:text-t1 disabled:cursor-default disabled:opacity-50"
        >
          {quoteOpen ? '− Requester’s exact words' : '+ Requester’s exact words'}
        </button>
        <Tip content="Nobody has to click approve — the routine sees `approved: true` from the outset. Rigg still only lands it clean, green, and with a reviewed_sha matching the head. Sending it back withdraws it.">
          <span>
            <Checkbox
              id="auto-merge"
              checked={autoMerge}
              onChange={setAutoMerge}
              label="Auto-merge"
            />
          </span>
        </Tip>
        {hasSmartAgent ? (
          <Tip content="Sends this to whichever smart routine is next in rotation instead of an ordinary one.">
            <span>
              <Checkbox id="smart" checked={smart} onChange={setSmart} label="Smart" />
            </span>
          </Tip>
        ) : null}
        <span className="flex-1" />
        <span className="hidden text-[11.5px] text-faint sm:inline">⌘↵ to send</span>
        <Button
          tone="primary"
          onClick={() => void send()}
          disabled={sending || uploading || (!prompt.trim() && !hasAttachment) || blocked}
        >
          {sending ? 'Sending…' : uploading ? 'Uploading…' : 'Send'}
        </Button>
      </div>
    </>
  );

  // The list is the point on a phone; a form with five fields is not
  // something to leave standing open under it. Collapsed to a "+", it opens
  // over the list instead of pushing it into a sliver.
  if (isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="New task"
          className="rg-focus fixed right-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-20 grid size-13 cursor-pointer place-items-center rounded-full bg-accent text-[24px] leading-none font-bold text-ink shadow-[0_10px_28px_rgba(20,24,42,0.35)] hover:brightness-110 active:brightness-95"
        >
          +
        </button>
        <Dialog open={open} onOpenChange={setOpen} title="New task">
          {form}
        </Dialog>
      </>
    );
  }

  return (
    <div className="shrink-0 border-t border-line bg-app px-4 pt-3.5 pb-4 sm:px-7">
      <div className="pb-2 text-[11.5px] font-semibold text-mute">New task</div>
      {form}
    </div>
  );
}
